// The deployable marks of the Marks field `deployable` (v2-the-deep.md
// §11.8, mockup round 068, R2538, R2652). The AUTO look `terrain-kin`
// draws its lane light in the look of mockup round 139 card 20, keyed on
// the chart reading (`render/lane-light-looks.ts`, R3072).
//
// One look draws all six kinds of §11.3: the Seine, the Cheval, the picket
// buoy, the lane light, the decoy buoy and the Palisade. Each look ports its
// card of `archive/mockups/068-deployables.html` point for point. A painter
// draws ONE deployable at the origin, +y down, one unit one map px, in the
// side ink the caller hands it. A line kind (the Seine, the Palisade) draws
// along +x from -L/2 to L/2, and the caller turns the context to the line.
//
// Every look draws the damage of §11.3 (pieces drop out as the hull share
// falls), the reach (the 200 px of the picket buoy, the 80 px of the lane
// light along an own shipping lane) and, where the card has one, the tell.
// The lane light of the AUTO look draws no reach: chart card 16 lights the
// road inside it (R3072).
// The figures a card draws to scale are pack figures, so the caller passes
// them in `DeployableMark`.
//
// A deployable draws in the world: its reach is hundreds of px, so the
// layer paints it, or bakes one sprite per kind and side, as the cost line
// of each card says. This module paints; it bakes nothing.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { DEPLOYABLE_STYLES } from '../gen/balance';
import { SeaObjectKind } from '../gen/proto/aquaphobia';
import {
  Fl, P, S, SEA_INK, circle, disc, hh, rot, text, type Ctx, type Pt,
} from './seaobject-kit';
import { TAU } from './math';
import { rgba } from './color';
import { paintLaneLight, type LaneLightMark, type LaneLightText, type LightSide } from './lane-light-looks';

/** One look of the `deployable` field. */
export type DeployableStyle = (typeof DEPLOYABLE_STYLES)[number];

/** The six kinds of §11.3, by the slug of the round page. */
export type DeployableKind = 'seine' | 'cheval' | 'picket' | 'lane' | 'decoy' | 'palisade';
export const DEPLOYABLE_KINDS: readonly DeployableKind[] = ['seine', 'cheval', 'picket', 'lane', 'decoy', 'palisade'];

/** The wire kind of each deployable kind (`SeaObjectKind`, R2644). */
export const DEPLOYABLE_KIND_OF: ReadonlyMap<SeaObjectKind, DeployableKind> = new Map([
  [SeaObjectKind.SEA_OBJECT_KIND_SEINE, 'seine'],
  [SeaObjectKind.SEA_OBJECT_KIND_CHEVAL, 'cheval'],
  [SeaObjectKind.SEA_OBJECT_KIND_PICKET_BUOY, 'picket'],
  [SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT, 'lane'],
  [SeaObjectKind.SEA_OBJECT_KIND_DECOY_BUOY, 'decoy'],
  [SeaObjectKind.SEA_OBJECT_KIND_PALISADE, 'palisade'],
] as [SeaObjectKind, DeployableKind][]);

/** The two kinds that lie across the heading of the Sapper (§11.3 rule 3). */
export const LINE_KINDS: ReadonlySet<DeployableKind> = new Set<DeployableKind>(['seine', 'palisade']);

/** What one painter is told about the deployable it draws. */
export interface DeployableMark {
  /** The side ink. */
  col: number;
  /** The hull share, 0 to 1: the damage the card draws. */
  hp: number;
  /** The clock in seconds, for a card with a tell. */
  t: number;
  /** False where the player turned the motion off. */
  motion: boolean;
  /** The length of a line kind, map px (the Seine 80, the Palisade 60). */
  L: number;
  /** The radius of the Cheval, map px. */
  R: number;
  /** The sonar of the picket buoy, map px. */
  reach: number;
  /** The reach of the lane light along an own shipping lane, map px. */
  laneReach: number;
  /** The unit legs of the own shipping lane at a lane light, or null: a
   *  hostile lane is a plan the viewer never sees. */
  legs: readonly Pt[] | null;
  /** The turn of a line kind on the map, radians: a shadow that falls the
   *  same way on every card turns back by it. */
  rot: number;
  /** The lane light of the AUTO look (R3072) reads these five:
   *  which side the light is to the viewer, whether it acts, the share of
   *  its anchor time that passed, the map zoom and the chart reading. A
   *  caller that leaves them out paints an own acting light in the modern
   *  reading. */
  side?: LightSide;
  armed?: boolean;
  anchor?: number;
  z?: number;
  historical?: boolean;
}

type Painter = (c: Ctx, o: DeployableMark) => void;
type Card = Record<DeployableKind, Painter>;

const INK = SEA_INK.deep;
const WHITE = SEA_INK.white;

// ---- the kit of round 068 ---------------------------------------------------------

const ink = (o: DeployableMark, a: number): string => rgba(o.col, Math.max(0, Math.min(1, a)));
/** Strokes the open path in the side ink. Round 068 keeps round caps on
 *  every dash. */
const so = (c: Ctx, o: DeployableMark, a: number, w: number, dash: readonly number[] = []): void => S(c, o.col, a, w, dash, true);
const fo = (c: Ctx, o: DeployableMark, a: number): void => Fl(c, o.col, a);
const seg = (c: Ctx, x0: number, y0: number, x1: number, y1: number): void => { c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); };
const arcP = (c: Ctx, x: number, y: number, r: number, a0: number, a1: number): void => { c.beginPath(); c.arc(x, y, r, a0, a1); };
const ringP = (c: Ctx, x: number, y: number, r: number): void => { c.beginPath(); c.arc(x, y, r, 0, TAU); };

/** Which of `n` pieces survive a hull share of `hp`: the lost ones spread
 *  by a hash, so a damaged mark stays the same mark frame to frame. */
function keep(n: number, hp: number, seed = 7): boolean[] {
  const cut = Math.round((1 - hp) * n);
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => hh(a, seed) - hh(b, seed));
  const k = new Array<boolean>(n).fill(true);
  for (let i = 0; i < cut; i++) k[order[i]] = false;
  return k;
}

/** `n` short arcs on the reach ring, each `span` radians wide. */
function reachArcs(c: Ctx, o: DeployableMark, n: number, span: number, a: number, w = 1, off = 0, dash: readonly number[] = []): void {
  for (let i = 0; i < n; i++) {
    const th = off + (i * TAU) / n;
    arcP(c, 0, 0, o.reach, th - span / 2, th + span / 2); so(c, o, a, w, dash);
  }
}

/** Calls `fn(ux, uy)` for each leg of the own shipping lane at the light. */
function legs(o: DeployableMark, fn: (ux: number, uy: number) => void): void {
  if (o.legs) for (const u of o.legs) fn(u[0], u[1]);
}

/** A dash that opens gaps as the hull share falls: solid at full hull. */
const hpDash = (hp: number, len: number): number[] => (hp >= 0.999 ? [] : [len * hp, len * (1 - hp)]);

/** A world direction seen from inside a turned line kind. */
const local = (o: DeployableMark, x: number, y: number): Pt => rot([x, y], -o.rot);

/** The brightness of a lamp that blinks with the period `p`. */
const blink = (t: number, p = 2): number => 0.45 + 0.55 * Math.abs(Math.sin((Math.PI * t) / p));

// ---- the cards ---------------------------------------------------------------------

/** Card 01 Danger Lines: the chart's own hazard language. */
const dangerLines: Card = {
  seine(c, o) {
    const h = o.L / 2, n = 20, kp = keep(n, o.hp, 3);
    for (let i = 0; i < n; i++) {
      if (!kp[i]) continue;
      const x = -h + ((i + 0.5) * o.L) / n;
      disc(c, x, -4.5, 1.15, o.col, 0.9); disc(c, x, 4.5, 1.15, o.col, 0.9);
    }
    for (let i = 0; i < 10; i++) {
      if (!kp[i * 2]) continue;
      const x = -h + i * 8;
      seg(c, x, -3.5, x + 8, 3.5); so(c, o, 0.45, 0.8); seg(c, x + 8, -3.5, x, 3.5); so(c, o, 0.45, 0.8);
    }
  },
  cheval(c, o) {
    const n = 40, kp = keep(n, o.hp, 5);
    for (let i = 0; i < n; i++) if (kp[i]) { const a = (i / n) * TAU; disc(c, Math.cos(a) * o.R, Math.sin(a) * o.R, 1.15, o.col, 0.9); }
    const cross = (x: number, y: number, s: number): void => {
      seg(c, x - s, y, x + s, y); so(c, o, 0.85, 1.2); seg(c, x, y - s, x, y + s); so(c, o, 0.85, 1.2);
      for (const [a, b] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) disc(c, x + a * s * 0.6, y + b * s * 0.6, 0.85, o.col, 0.8);
    };
    cross(0, 0, 4.5);
    for (let i = 0; i < 4; i++) { const a = Math.PI / 4 + (i * Math.PI) / 2; cross(Math.cos(a) * 16, Math.sin(a) * 16, 3.4); }
  },
  picket(c, o) {
    reachArcs(c, o, 4, 0.14, 0.45, 1.5, Math.PI / 4, [0.1, 4]);
    ringP(c, 0, 0, 3.2); so(c, o, 0.95, 1.4); disc(c, 0, 0, 1, o.col, 0.95);
    const kp = keep(4, o.hp, 2);
    ([[-1, 6.5], [1, 6.5], [-1, 9.5], [1, 9.5]] as const).forEach(([s, r], i) => {
      if (!kp[i]) return;
      const a0 = s < 0 ? Math.PI : 0;
      arcP(c, 0, 0, r, a0 - 0.6, a0 + 0.6); so(c, o, 0.85, 1.2);
    });
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      const x = ux * o.laneReach, y = uy * o.laneReach;
      seg(c, x - uy * 10, y + ux * 10, x + uy * 10, y - ux * 10); so(c, o, 0.8, 1.6, [0.1, 3.2]);
    });
    disc(c, 0, 0, 2.2, o.col, 0.95);
    const f = o.motion ? blink(o.t, 4) : 1;
    for (let i = 0; i < 8; i++) {
      const a = (i * TAU) / 8 + Math.PI / 8;
      seg(c, Math.cos(a) * 4, Math.sin(a) * 4, Math.cos(a) * 8.5, Math.sin(a) * 8.5); so(c, o, 0.9 * f, 1.2);
    }
  },
  decoy(c, o) {
    for (let i = 0; i < 14; i++) { const a = (i / 14) * TAU; disc(c, Math.cos(a) * 7, Math.sin(a) * 7, 1.05, o.col, 0.9); }
  },
  palisade(c, o) {
    const h = o.L / 2;
    seg(c, -h, 0, h, 0); so(c, o, 0.95, 3.2);
    const n = 17, kp = keep(n, o.hp, 9);
    for (let i = 0; i < n; i++) {
      if (!kp[i]) continue;
      const x = -h + 1.5 + (i * (o.L - 3)) / (n - 1);
      seg(c, x, -2, x - 1.5, -6.5); so(c, o, 0.8, 1); seg(c, x, 2, x - 1.5, 6.5); so(c, o, 0.8, 1);
    }
  },
};

/** Card 02 Terrain Kin: the terrain language of ui-map-interface.md §4.4,
 *  so the Palisade reads as rock. */
const terrainKin: Omit<Card, 'lane'> = {
  seine(c, o) {
    const h = o.L / 2;
    seg(c, -h, 0, h, 0); so(c, o, 0.8, 1);
    const n = 20, kp = keep(n, o.hp, 4);
    for (let i = 0; i < n; i++) {
      if (!kp[i]) continue;
      const x = -h + 2 + i * 4, s = i % 2 ? 1 : -1;
      seg(c, x, 0, x, s * 5.5); so(c, o, 0.75, 1.1);
    }
  },
  cheval(c, o) {
    ringP(c, 0, 0, o.R); so(c, o, 0.55, 1, [2, 3]);
    const kp = keep(90, o.hp, 6);
    for (let i = 0; i < 90; i++) {
      if (!kp[i]) continue;
      const r = o.R * Math.sqrt(hh(i, 11)) * 0.95, a = hh(i, 12) * TAU;
      disc(c, Math.cos(a) * r, Math.sin(a) * r, 0.75, o.col, 0.75);
    }
  },
  picket(c, o) {
    ringP(c, 0, 0, o.reach); so(c, o, 0.1, 1, [6, 6]);
    ringP(c, 0, 0, 7); so(c, o, 0.55, 1, hpDash(o.hp, 4));
    ringP(c, 0, 0, 3.8); so(c, o, 0.9, 1.3, hpDash(o.hp, 3));
    disc(c, 0, 0, 1.3, o.col, 0.95);
  },
  decoy(c, o) {
    ringP(c, 0, 0, 6.5); so(c, o, 0.9, 1.3);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * TAU;
      seg(c, Math.cos(a) * 6.5, Math.sin(a) * 6.5, Math.cos(a) * 3, Math.sin(a) * 3); so(c, o, 0.6, 1);
    }
  },
  palisade(c, o) {
    const stadium = (hw: number, r: number): void => {
      c.beginPath(); c.moveTo(-hw + r, -r); c.lineTo(hw - r, -r);
      c.arc(hw - r, 0, r, -Math.PI / 2, Math.PI / 2); c.lineTo(-hw + r, r);
      c.arc(-hw + r, 0, r, Math.PI / 2, Math.PI * 1.5); c.closePath();
    };
    stadium(o.L / 2, 6); Fl(c, SEA_INK.rockFill, SEA_INK.rockFillAlpha);
    SEA_INK.rockRings.slice(1, 4).forEach((ring, k) => {
      stadium(o.L / 2 - 2.5 - k * 2.4, 3.6 - k * 1.1);
      c.lineWidth = 1.1; c.strokeStyle = rgba(ring.color, ring.alpha); c.stroke();
    });
    stadium(o.L / 2, 6); so(c, o, 0.95, 1.6, hpDash(o.hp, 9));
  },
};

/** Card 04 Sidescan Returns: the image of a sidescan sonar. Each object
 *  casts an acoustic shadow to the lower right. */
const sidescan: Card = {
  seine(c, o) {
    const h = o.L / 2, sd = local(o, 0.55, 0.83), kp = keep(26, o.hp, 4);
    for (let i = 0; i < 26; i++) {
      if (!kp[i]) continue;
      const x = -h + 1.5 + i * 3.08, y = (hh(i, 3) - 0.5) * 3, r = 0.7 + hh(i, 4) * 0.7;
      seg(c, x, y, x + sd[0] * 5, y + sd[1] * 5); c.lineWidth = 1.4; c.strokeStyle = rgba(INK, 0.4); c.stroke();
      disc(c, x, y, r, o.col, 0.75);
    }
  },
  cheval(c, o) {
    const kp = keep(52, o.hp, 6);
    for (let i = 0; i < 52; i++) {
      if (!kp[i]) continue;
      const ring = i < 40;
      const a = ring ? (i / 40) * TAU + hh(i, 1) * 0.08 : hh(i, 2) * TAU;
      const r = ring ? o.R + (hh(i, 3) - 0.5) * 3 : o.R * 0.75 * Math.sqrt(hh(i, 4));
      const x = Math.cos(a) * r, y = Math.sin(a) * r;
      seg(c, x, y, x + 0.55 * 4, y + 0.83 * 4); c.lineWidth = 1.3; c.strokeStyle = rgba(INK, 0.45); c.stroke();
      disc(c, x, y, 0.8 + hh(i, 5) * 0.5, o.col, 0.8);
    }
  },
  picket(c, o) {
    for (let i = 0; i < 24; i++) { const a = (i / 24) * TAU; disc(c, Math.cos(a) * o.reach, Math.sin(a) * o.reach, 0.9, o.col, 0.3); }
    P(c, [[-1, 0], [1, 0], [1 + 0.55 * 15, 0.83 * 15], [-1 + 0.55 * 15, 0.83 * 15]]); Fl(c, INK, 0.55);
    disc(c, 0, 0, 2.6, o.col, 0.6 + 0.35 * o.hp); circle(c, 0, 0, 4.5, o.col, 0.3 * o.hp, 1);
  },
  lane(c, o) {
    legs(o, (ux, uy) => { for (let d = 10; d <= o.laneReach; d += 8) disc(c, ux * d, uy * d, 0.95, o.col, 0.55); });
    const g = c.createRadialGradient(0, 0, 0, 0, 0, 11);
    g.addColorStop(0, ink(o, 0.5)); g.addColorStop(1, ink(o, 0));
    c.fillStyle = g; ringP(c, 0, 0, 11); c.fill();
    disc(c, 0, 0, 2.6, o.col, 0.95); disc(c, 0, 0, 1, WHITE, 0.8);
  },
  decoy(c, o) {
    for (let i = 0; i < 11; i++) {
      const a = (i / 11) * TAU + hh(i, 9) * 0.2, r = 5.5 + (hh(i, 8) - 0.5) * 1.6;
      disc(c, Math.cos(a) * r, Math.sin(a) * r, 0.75 + hh(i, 7) * 0.45, o.col, 0.8);
    }
  },
  palisade(c, o) {
    const h = o.L / 2, sd = local(o, 0.55, 0.83);
    P(c, [[-h, 0], [h, 0], [h + sd[0] * 20, sd[1] * 20], [-h + sd[0] * 20, sd[1] * 20]]); Fl(c, INK, 0.62);
    seg(c, -h, 0, h, 0); so(c, o, 0.95, 4, hpDash(o.hp, 10)); seg(c, -h, 0, h, 0); so(c, o, 0.35, 4);
  },
};

/** Card 05 Blueprint Section: the engineering drawing, in the line types of
 *  the draughtsman. */
const blueprint = (radiusWord: (r: number) => string, face: string): Card => ({
  seine(c, o) {
    const h = o.L / 2;
    seg(c, -h - 6, 0, h + 6, 0); so(c, o, 0.5, 0.8, [6, 2, 1, 2]);
    c.beginPath(); c.rect(-h, -3, o.L, 6); so(c, o, 0.9, 1);
    for (const x of [-h, h]) { seg(c, x, -8, x, 8); so(c, o, 0.6, 0.8); }
    const kp = keep(10, o.hp, 2);
    for (let i = 0; i < 10; i++) {
      if (!kp[i]) continue;
      const x = -h + i * 8;
      seg(c, x, -3, x + 8, 3); so(c, o, 0.55, 0.8); seg(c, x + 8, -3, x, 3); so(c, o, 0.55, 0.8);
    }
  },
  cheval(c, o) {
    seg(c, -o.R - 6, 0, o.R + 6, 0); so(c, o, 0.45, 0.8, [6, 2, 1, 2]);
    seg(c, 0, -o.R - 6, 0, o.R + 6); so(c, o, 0.45, 0.8, [6, 2, 1, 2]);
    c.save(); c.beginPath(); c.arc(0, 0, o.R, 0, TAU); c.arc(0, 0, o.R - 4, 0, TAU, true); c.clip();
    const kp = keep(24, o.hp, 4);
    for (let i = 0; i < 24; i++) { if (!kp[i]) continue; const k = -36 + i * 3; seg(c, k - 40, -40, k + 40, 40); so(c, o, 0.75, 0.9); }
    c.restore();
    ringP(c, 0, 0, o.R); so(c, o, 0.95, 1.3); ringP(c, 0, 0, o.R - 4); so(c, o, 0.8, 1);
  },
  picket(c, o) {
    const a = -1.05, ca = Math.cos(a), sa = Math.sin(a);
    seg(c, ca * 10, sa * 10, ca * o.reach, sa * o.reach); so(c, o, 0.3, 0.8);
    seg(c, ca * o.reach - sa * 5, sa * o.reach + ca * 5, ca * o.reach + sa * 5, sa * o.reach - ca * 5); so(c, o, 0.55, 1);
    // The radius dimension stands at three quarters of the leader.
    c.save(); c.translate(ca * o.reach * 0.75, sa * o.reach * 0.75); c.rotate(a);
    text(c, radiusWord(o.reach), 0, -5, `400 7px ${face}`, o.col, 0.6);
    c.restore();
    ringP(c, 0, 0, 8); so(c, o, 0.6, 1, [2, 1.6]);
    ringP(c, 0, 0, 4); so(c, o, 0.95, 1.4, hpDash(o.hp, 4));
    seg(c, -2.2, 0, 2.2, 0); so(c, o, 0.9, 1); seg(c, 0, -2.2, 0, 2.2); so(c, o, 0.9, 1);
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      const nx = -uy * 13, ny = ux * 13;
      seg(c, nx, ny, ux * o.laneReach + nx, uy * o.laneReach + ny); so(c, o, 0.5, 0.8);
      for (const d of [0, o.laneReach]) { seg(c, ux * d + nx * 0.6, uy * d + ny * 0.6, ux * d + nx * 1.4, uy * d + ny * 1.4); so(c, o, 0.6, 0.8); }
    });
    ringP(c, 0, 0, 5); so(c, o, 0.95, 1.3);
    for (const a0 of [0, Math.PI]) { c.beginPath(); c.moveTo(0, 0); c.arc(0, 0, 5, a0 - Math.PI / 2, a0); c.closePath(); fo(c, o, 0.95); }
  },
  decoy(c, o) {
    const ph = [6, 1.5, 1.5, 1.5, 1.5, 1.5];
    ringP(c, 0, 0, 6); so(c, o, 0.9, 1.2, ph);
    seg(c, -10, 0, 10, 0); so(c, o, 0.55, 0.8, ph); seg(c, 0, -10, 0, 10); so(c, o, 0.55, 0.8, ph);
  },
  palisade(c, o) {
    const h = o.L / 2;
    c.save(); c.beginPath(); c.rect(-h, -4.5, o.L, 9); c.clip();
    const kp = keep(24, o.hp, 5);
    for (let i = 0; i < 24; i++) { if (!kp[i]) continue; const k = -h - 6 + i * 3; seg(c, k, 6, k + 12, -6); so(c, o, 0.85, 1); }
    c.restore();
    c.beginPath(); c.rect(-h, -4.5, o.L, 9); so(c, o, 0.95, 1.6);
  },
});

/** Card 07 Obstacle Symbols: the military map of the land engineer. */
const obstacles: Card = {
  seine(c, o) {
    const h = o.L / 2;
    seg(c, -h, 0, h, 0); so(c, o, 0.85, 1.1);
    const kp = keep(10, o.hp, 3);
    for (let i = 0; i < 10; i++) { if (!kp[i]) continue; ringP(c, -h + 4 + i * 8, 0, 2.8); so(c, o, 0.9, 1.1); }
    if (o.hp < 1) { seg(c, -6, 9, 6, -9); so(c, o, 0.95, 1.5); }
  },
  cheval(c, o) {
    ringP(c, 0, 0, o.R); so(c, o, 0.85, 1.1);
    const kp = keep(15, o.hp, 6);
    for (let i = 0; i < 15; i++) {
      if (!kp[i]) continue;
      const a = (i / 15) * TAU, x = Math.cos(a) * o.R, y = Math.sin(a) * o.R;
      seg(c, x - 2.6, y - 2.6, x + 2.6, y + 2.6); so(c, o, 0.95, 1.2); seg(c, x - 2.6, y + 2.6, x + 2.6, y - 2.6); so(c, o, 0.95, 1.2);
    }
  },
  picket(c, o) {
    ringP(c, 0, 0, o.reach); so(c, o, 0.25, 1, [2, 14]);
    P(c, [[0, -6.5], [5.7, 3.3], [-5.7, 3.3]]); so(c, o, 0.95, 1.4); disc(c, 0, -0.3, 1.3, o.col, 0.95);
    if (o.hp < 1) { seg(c, -6, 7, 6, -9); so(c, o, 0.95, 1.4); }
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      const x = ux * o.laneReach, y = uy * o.laneReach, nx = -uy, ny = ux;
      seg(c, x - nx * 11, y - ny * 11, x + nx * 11, y + ny * 11); so(c, o, 0.8, 1.6);
      for (const s of [-1, 1]) { seg(c, x + nx * 11 * s, y + ny * 11 * s, x + nx * 11 * s - ux * 4, y + ny * 11 * s - uy * 4); so(c, o, 0.8, 1.2); }
    });
    ringP(c, 0, 0, 6); so(c, o, 0.9, 1.2);
    P(c, [[0, -4.6], [1.2, -1.2], [4.6, 0], [1.2, 1.2], [0, 4.6], [-1.2, 1.2], [-4.6, 0], [-1.2, -1.2]]); fo(c, o, 0.95);
  },
  decoy(c, o) {
    P(c, [[0, -4], [5.7, 5.8], [-5.7, 5.8]]); so(c, o, 0.9, 1.3, [2.5, 1.8]);
    P(c, [[-7, -6], [0, -12], [7, -6]], false); so(c, o, 0.9, 1.3, [2.5, 1.8]);
  },
  palisade(c, o) {
    const h = o.L / 2;
    seg(c, -h, 0, h, 0); so(c, o, 0.95, 3.6);
    for (const [x, s] of [[-h, 7], [0, 5], [h, 7]]) { seg(c, x, -s, x, s); so(c, o, 0.95, 2.4); }
    if (o.hp < 1) { seg(c, 8, 10, 20, -10); so(c, o, 0.95, 1.5); }
  },
};

/** Card 08 Hazard Stripes: the barrier stripe of the works site. */
const hazard: Card = {
  seine(c, o) {
    const h = o.L / 2, kp = keep(10, o.hp, 3);
    for (let i = 0; i < 10; i++) {
      if (!kp[i]) continue;
      const x0 = -h + i * 8;
      c.save(); c.beginPath(); c.rect(x0, -1.8, 8, 3.6); c.clip(); Fl(c, INK, 0.6);
      for (let k = -1; k < 3; k++) { seg(c, x0 + k * 4, 3, x0 + k * 4 + 5, -3); so(c, o, 0.95, 1.6); }
      c.restore();
    }
  },
  cheval(c, o) {
    disc(c, 0, 0, o.R - 4, o.col, 0.05);
    const kp = keep(36, o.hp, 6);
    for (let i = 0; i < 36; i++) {
      const a0 = (i / 36) * TAU, a1 = a0 + TAU / 36;
      c.beginPath(); c.arc(0, 0, o.R, a0, a1); c.arc(0, 0, o.R - 4, a1, a0, true); c.closePath();
      if (i % 2) Fl(c, INK, 0.65); else fo(c, o, kp[i] ? 0.95 : 0.2);
    }
  },
  picket(c, o) {
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2, x = Math.cos(a) * o.reach, y = Math.sin(a) * o.reach;
      c.save(); c.translate(x, y); c.rotate(a + Math.PI / 2);
      c.beginPath(); c.rect(-6, -1.5, 12, 3); c.clip(); Fl(c, INK, 0.5);
      for (let k = -2; k < 3; k++) { seg(c, k * 4, 2, k * 4 + 3, -2); so(c, o, 0.6, 1.4); }
      c.restore();
    }
    c.save(); c.beginPath(); c.rect(-4.5, -4.5, 9, 9); c.clip(); Fl(c, INK, 0.6);
    for (let k = -2; k < 3; k++) { seg(c, k * 4 - 5, 5, k * 4 + 5, -5); so(c, o, (k + 2) / 5 < o.hp ? 0.95 : 0.2, 1.7); }
    c.restore();
    c.beginPath(); c.rect(-4.5, -4.5, 9, 9); so(c, o, 0.95, 1);
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      c.save(); c.rotate(Math.atan2(uy, ux));
      for (const y of [-9, 9]) for (let d = 8; d < o.laneReach; d += 6) { seg(c, d, y, d + 3, y); so(c, o, 0.75, 1.6); }
      c.restore();
    });
    c.save(); ringP(c, 0, 0, 5); c.clip(); Fl(c, INK, 0.6);
    for (let k = -2; k < 3; k++) { seg(c, k * 3.6 - 6, 6, k * 3.6 + 6, -6); so(c, o, 0.95, 1.5); }
    c.restore();
    ringP(c, 0, 0, 5); so(c, o, 0.95, 1);
  },
  decoy(c, o) {
    c.beginPath(); c.rect(-4.5, -4.5, 9, 9); so(c, o, 0.9, 1.2, [2.2, 1.6]);
  },
  palisade(c, o) {
    const h = o.L / 2, n = 15, kp = keep(n, o.hp, 9);
    c.save(); c.beginPath(); c.rect(-h, -4, o.L, 8); c.clip(); Fl(c, INK, 0.75);
    for (let i = 0; i < n; i++) {
      const x = -h - 4 + i * 4.6;
      P(c, [[x, 4], [x + 2.4, 4], [x + 6.4, -4], [x + 4, -4]]); fo(c, o, kp[i] ? 0.95 : 0.22);
    }
    c.restore();
    c.beginPath(); c.rect(-h, -4, o.L, 8); so(c, o, 0.9, 1);
  },
};

/** Card 11 Light Rhythms: the light characters of the List of Lights. Each
 *  light runs its own character on a 6 s cycle. */
const lightOn = (o: DeployableMark, wins: readonly (readonly [number, number])[]): number => {
  if (!o.motion) return 1;
  const p = o.t % 6;
  return wins.some(([a, b]) => p >= a && p < b) ? 1 : 0.5;
};
const lightRhythms: Card = {
  seine(c, o) {
    const h = o.L / 2, kp = keep(20, o.hp, 3);
    const f = lightOn(o, [[0, 0.3], [1, 1.3], [2, 2.3], [3, 3.3], [4, 4.3], [5, 5.3]]);
    for (let i = 0; i < 20; i++) { const x = -h + i * 4; seg(c, x + 0.6, 0, x + 2.6, 0); so(c, o, kp[i] ? 0.95 * f : 0.12, 1.5); }
    disc(c, -h - 2, 0, 1.8, o.col, 0.95); disc(c, h + 2, 0, 1.8, o.col, 0.95);
  },
  cheval(c, o) {
    const kp = keep(10, o.hp, 6), f = lightOn(o, [[0, 3]]);
    for (let i = 0; i < 10; i++) { const a = (i / 10) * TAU; arcP(c, 0, 0, o.R, a, a + TAU / 20); so(c, o, kp[i] ? 0.95 * f : 0.12, 2); }
  },
  picket(c, o) {
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2 + Math.PI / 4;
      for (const d of [-0.025, 0.025]) { arcP(c, 0, 0, o.reach, a + d * 2 - 0.02, a + d * 2 + 0.02); so(c, o, 0.35, 1.4); }
    }
    const kp = keep(4, o.hp, 2), f = lightOn(o, [[0, 0.4], [0.9, 1.3]]);
    disc(c, 0, 0, 2, o.col, 0.95);
    [-0.38, 0.38, Math.PI - 0.38, Math.PI + 0.38].forEach((a, i) => { arcP(c, 0, 0, 8, a - 0.22, a + 0.22); so(c, o, kp[i] ? 0.95 * f : 0.12, 1.8); });
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      c.save(); c.rotate(Math.atan2(uy, ux));
      for (const y of [-8, 8]) { seg(c, 9, y, o.laneReach, y); so(c, o, 0.55, 1.2, [10, 3]); }
      c.restore();
    });
    const f = lightOn(o, [[0.8, 6]]);
    disc(c, 0, 0, 2.6, o.col, 0.95); arcP(c, 0, 0, 8, 0.4, TAU - 0.4); so(c, o, 0.95 * f, 1.8);
  },
  decoy(c, o) {
    const f = lightOn(o, [[0, 0.3], [0.6, 0.9], [1.2, 1.5]]);
    for (const a of [-Math.PI / 2 - 0.7, -Math.PI / 2, -Math.PI / 2 + 0.7, Math.PI / 2 - 0.7, Math.PI / 2, Math.PI / 2 + 0.7]) {
      arcP(c, 0, 0, 8, a - 0.2, a + 0.2); so(c, o, 0.95 * f, 1.8);
    }
  },
  palisade(c, o) {
    const h = o.L / 2;
    seg(c, -h, 0, h, 0); so(c, o, 0.13, 11); seg(c, -h, 0, h, 0); so(c, o, 0.95, 4);
    if (o.hp < 1) {
      seg(c, h - o.L * (1 - o.hp), 0, h, 0);
      c.lineWidth = 2; c.strokeStyle = rgba(INK, 0.8); c.lineCap = 'butt'; c.stroke();
    }
  },
};

/** Card 13 Hardware: the real objects, seen from above. */
const hardware: Card = {
  seine(c, o) {
    const h = o.L / 2, kp = keep(8, o.hp, 3);
    c.save(); c.beginPath(); c.rect(-h, 1, o.L, 8); c.clip();
    for (let i = -1; i < 11; i++) {
      const x = -h + i * 8, a = kp[(i + 8) % 8] ? 0.4 : 0.1;
      seg(c, x, 1, x + 8, 9); so(c, o, a, 0.7); seg(c, x + 8, 1, x, 9); so(c, o, a, 0.7);
    }
    c.restore();
    seg(c, -h, 0, h, 0); so(c, o, 0.85, 1);
    for (let i = 0; i < 8; i++) { if (!kp[i]) continue; c.beginPath(); c.ellipse(-h + 5 + i * 10, 0, 2.8, 1.8, 0, 0, TAU); fo(c, o, 0.95); }
  },
  cheval(c, o) {
    const kp = keep(22, o.hp, 6);
    for (let i = 0; i < 22; i++) {
      if (!kp[i]) continue;
      const a = (i / 22) * TAU;
      c.beginPath(); c.ellipse(Math.cos(a) * (o.R - 3.5), Math.sin(a) * (o.R - 3.5), 5.2, 3, a + Math.PI / 2, 0, TAU); so(c, o, 0.85, 1);
    }
  },
  picket(c, o) {
    ringP(c, 0, 0, o.reach); so(c, o, 0.08, 1);
    ringP(c, 0, 0, 4.8); so(c, o, 0.95, 2.4, hpDash(o.hp, 5));
    seg(c, -2.2, 0, 2.2, 0); so(c, o, 0.95, 1); seg(c, 0, -2.2, 0, 2.2); so(c, o, 0.95, 1);
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      const g = c.createLinearGradient(0, 0, ux * o.laneReach, uy * o.laneReach);
      g.addColorStop(0, ink(o, 0.22)); g.addColorStop(1, ink(o, 0));
      seg(c, 0, 0, ux * o.laneReach, uy * o.laneReach); c.lineWidth = 12; c.lineCap = 'butt'; c.strokeStyle = g; c.stroke();
    });
    ringP(c, 0, 0, 7.5); so(c, o, 0.18, 1); disc(c, 0, 0, 4.3, o.col, 0.6); disc(c, 0, 0, 1.9, WHITE, 0.95);
  },
  decoy(c, o) {
    c.beginPath(); c.rect(-6, -1.8, 3, 3.6); fo(c, o, 0.95);
    P(c, [[-3, -1.8], [1, -4.6], [1, 4.6], [-3, 1.8]]); so(c, o, 0.95, 1.2);
    for (const r of [4, 6.8]) { arcP(c, -1, 0, r, -0.7, 0.7); so(c, o, 0.85, 1.1); }
  },
  palisade(c, o) {
    const h = o.L / 2, n = 5, bw = (o.L - (n - 1) * 1.4) / n, kp = keep(n, o.hp, 4);
    for (let i = 0; i < n; i++) {
      const x = -h + i * (bw + 1.4);
      c.beginPath(); c.rect(x, -4.5, bw, 9); fo(c, o, kp[i] ? 0.6 : 0.2); so(c, o, 0.95, 1.2);
      if (!kp[i]) {
        P(c, [[x + 2, -4.5], [x + 5, -1], [x + 3.5, 1.5], [x + 7, 4.5]], false);
        c.lineWidth = 1; c.strokeStyle = rgba(INK, 0.9); c.stroke();
      }
    }
  },
};

/** Card 22 Acoustic Shapes: each deployable drawn as what it does to sound.
 *  The lane light pulses on a 2 s cycle. */
const acoustic: Card = {
  seine(c, o) {
    const h = o.L / 2;
    for (const s of [1, -1]) {
      c.beginPath();
      for (let x = -h; x <= h; x += 1) {
        const y = s * 3.6 * Math.sin((4 * Math.PI * (x + h)) / o.L);
        if (x === -h) c.moveTo(x, y); else c.lineTo(x, y);
      }
      so(c, o, 0.92, 1.2, hpDash(o.hp, 7));
    }
  },
  cheval(c, o) {
    ringP(c, 0, 0, o.R); so(c, o, 0.92, 1.5, hpDash(o.hp, 9));
    ringP(c, 0, 0, o.R * (22 / 30)); so(c, o, 0.7, 1.1, [3, 3]);
    ringP(c, 0, 0, o.R * (14 / 30)); so(c, o, 0.55, 1, [2, 4]);
    disc(c, 0, 0, 1.6, o.col, 0.9);
  },
  picket(c, o) {
    for (let i = 0; i < 6; i++) { const a = (i / 6) * TAU + 0.3; arcP(c, 0, 0, o.reach, a - 0.06, a + 0.06); so(c, o, 0.3, 1.2); }
    disc(c, 0, 0, 2, o.col, 0.95);
    const kp = keep(4, o.hp, 2);
    for (let i = 0; i < 4; i++) {
      if (!kp[i]) continue;
      const a = Math.PI / 4 + (i * Math.PI) / 2, ca = Math.cos(a), sa = Math.sin(a);
      arcP(c, ca * 10, sa * 10, 4.5, a + Math.PI - 0.8, a + Math.PI + 0.8); so(c, o, 0.9, 1.3);
      seg(c, ca * 6.4, sa * 6.4, ca * 4.2, sa * 4.2); so(c, o, 0.9, 1.1);
    }
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      const a = Math.atan2(uy, ux);
      for (let d = 16; d <= o.laneReach; d += 16) { arcP(c, ux * (d - 6), uy * (d - 6), 6, a - 0.7, a + 0.7); so(c, o, 0.55, 1.1); }
    });
    disc(c, 0, 0, 3, o.col, 0.95);
    if (o.motion) {
      for (const d of [0, 0.5]) { const p = (o.t / 2 + d) % 1; ringP(c, 0, 0, 3.5 + p * 9); so(c, o, 0.8 * (1 - p), 1.2); }
    } else { ringP(c, 0, 0, 6.5); so(c, o, 0.6, 1.2); ringP(c, 0, 0, 10); so(c, o, 0.3, 1.2); }
  },
  decoy(c, o) {
    c.beginPath(); c.ellipse(0, 0, 11, 4.6, -0.5, 0, TAU); so(c, o, 0.9, 1.2, [2.6, 2]);
    c.beginPath(); c.ellipse(0, 0, 6, 2.4, -0.5, 0, TAU); so(c, o, 0.7, 1, [1.6, 1.6]);
  },
  palisade(c, o) {
    const h = o.L / 2;
    seg(c, -h, 0, h, 0); so(c, o, 0.95, 4.5);
    for (const [y, a, k] of [[6, 0.45, 0.9], [9.5, 0.2, 0.78]]) {
      for (const s of [-1, 1]) { seg(c, -h * k, s * y, h * k, s * y); so(c, o, a, 1, hpDash(o.hp, 6)); }
    }
  },
};

/** Card 23 Effect Fields: each deployable draws the water it acts on, and
 *  its body is only a small core. */
const effectFields: Card = {
  seine(c, o) {
    const h = o.L / 2, g = c.createLinearGradient(0, -12, 0, 12);
    g.addColorStop(0, ink(o, 0)); g.addColorStop(0.5, ink(o, 0.24 * o.hp)); g.addColorStop(1, ink(o, 0));
    c.fillStyle = g; c.fillRect(-h, -12, o.L, 24);
    seg(c, -h, 0, h, 0); so(c, o, 0.9, 1.2, hpDash(o.hp, 8));
  },
  cheval(c, o) {
    disc(c, 0, 0, o.R, o.col, 0.17 * o.hp); ringP(c, 0, 0, o.R); so(c, o, 0.9, 1.6); disc(c, 0, 0, 1.6, o.col, 0.9);
  },
  picket(c, o) {
    const g = c.createRadialGradient(0, 0, 0, 0, 0, o.reach);
    g.addColorStop(0, ink(o, 0.09)); g.addColorStop(1, ink(o, 0));
    c.fillStyle = g; ringP(c, 0, 0, o.reach); c.fill();
    ringP(c, 0, 0, 3.6); so(c, o, 0.95, 1.4, hpDash(o.hp, 4)); disc(c, 0, 0, 1.2, o.col, 0.95);
  },
  lane(c, o) {
    legs(o, (ux, uy) => {
      const g = c.createLinearGradient(0, 0, ux * o.laneReach, uy * o.laneReach);
      g.addColorStop(0, ink(o, 0.26)); g.addColorStop(1, ink(o, 0));
      seg(c, 0, 0, ux * o.laneReach, uy * o.laneReach); c.lineWidth = 16; c.lineCap = 'butt'; c.strokeStyle = g; c.stroke();
    });
    ringP(c, 0, 0, 5); so(c, o, 0.4, 1); disc(c, 0, 0, 2.6, o.col, 0.95);
  },
  decoy(c, o) {
    c.save(); c.rotate(-0.5); c.scale(1, 0.45);
    const g = c.createRadialGradient(0, 0, 0, 0, 0, 12);
    g.addColorStop(0, ink(o, 0.4)); g.addColorStop(1, ink(o, 0));
    c.fillStyle = g; ringP(c, 0, 0, 12); c.fill();
    c.restore();
    ringP(c, 0, 0, 2.2); so(c, o, 0.95, 1.2);
  },
  palisade(c, o) {
    const h = o.L / 2;
    c.fillStyle = ink(o, 0.95); c.fillRect(-h, -3, o.L * o.hp, 6);
    c.beginPath(); c.rect(-h, -3, o.L, 6); c.lineWidth = 1.2; c.strokeStyle = ink(o, 0.95); c.lineJoin = 'miter'; c.stroke();
  },
};

/** The words and the face the blueprint dimension writes in. The caller
 *  passes the mono stack and the word of the locale. */
export interface DeployableText {
  /** The radius dimension of the picket buoy, for example `R200`. */
  radius: (r: number) => string;
  /** The font family of the figure (the mono stack). */
  face: string;
  /** The light character of the lane light of the AUTO look (R2723). */
  light: LaneLightText;
}

/** The lane light of the AUTO look: card 20 of round 139 (R3072, R2723).
 *  It draws no reach, because chart card 16 lights the road. One scratch
 *  mark, so a paint allocates nothing. */
function laneLightOf(words: LaneLightText): Painter {
  const m: LaneLightMark = { col: 0, side: 'own', armed: true, anchor: 1, z: 1, historical: false };
  return (c, o) => {
    m.col = o.col; m.side = o.side ?? 'own'; m.armed = o.armed ?? true; m.anchor = o.anchor ?? 1;
    m.z = o.z ?? 1; m.historical = o.historical ?? false;
    paintLaneLight(c, m, words);
  };
}

/**
 * The painters of each look, built with the words of the caller. The
 * record is typed by the list, so a look the list gains without its card is
 * a type error.
 */
export function deployableCards(words: DeployableText): Record<DeployableStyle, Card> {
  return {
    'terrain-kin': { ...terrainKin, lane: laneLightOf(words.light) },
    'danger-lines': dangerLines,
    'sidescan-returns': sidescan,
    'blueprint-section': blueprint(words.radius, words.face),
    'obstacle-symbols': obstacles,
    'hazard-stripes': hazard,
    'light-rhythms': lightRhythms,
    hardware,
    'acoustic-shapes': acoustic,
    'effect-fields': effectFields,
  };
}

/** The looks whose card draws a tell that moves with the clock: the light
 *  rhythms, the blink of the danger line light and the pulse of the
 *  acoustic lane light. With the motion off they hold still. */
export const MOVING_DEPLOYABLE_STYLES: ReadonlySet<DeployableStyle> = new Set<DeployableStyle>([
  'danger-lines', 'light-rhythms', 'acoustic-shapes',
]);
